import { Icon } from "@iconify/react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import FeatureSectionAIDemo from "../features/FeatureSectionAIDemo";
import FeatureSectionAgentEnterprise from "../features/FeatureSectionAgentEnterprise";
import FeatureSectionPDF from "../features/FeatureSectionPDF";
import FeatureSectionRecording from "../features/FeatureSectionRecording";
import {
  redirectForChat,
  redirectToApp,
} from "../../../utils/landingRedirect";
import type { FeatureAction } from "../../../utils/landingRedirect";

const SLIDE_COUNT = 4;
const AUTO_ROTATE_MS = 5000;

type HomeActionCard = {
  id: "meeting" | "knowledge" | "agent" | "creation" | "learning";
  titleKey: string;
  subtitleKey: string;
  icon?: string;
  imageSrc?: string;
  action: FeatureAction;
};

type ModelFamily = {
  id: string;
  label: string;
  detail: string;
  icon: string;
  color: string;
};

type PlatformCard = {
  id: string;
  titleKey: string;
  icon: string;
  feature: string;
  cta?: boolean;
};

const homeActionCards: HomeActionCard[] = [
  {
    id: "meeting",
    titleKey: "modern.interactiveHero.hero.entryCards.record.title",
    subtitleKey: "modern.interactiveHero.hero.entryCards.record.subtitle",
    icon: "ion:mic-outline",
    action: "realtime_recording",
  },
  {
    id: "knowledge",
    titleKey: "modern.interactiveHero.hero.entryCards.upload.title",
    subtitleKey: "modern.interactiveHero.hero.entryCards.upload.subtitle",
    icon: "ion:cloud-upload-outline",
    action: "attach_file",
  },
  {
    id: "agent",
    titleKey: "modern.interactiveHero.hero.entryCards.agent.title",
    subtitleKey: "modern.interactiveHero.hero.entryCards.agent.subtitle",
    imageSrc: "/logo/logo.png",
    action: "chat",
  },
  {
    id: "creation",
    titleKey: "modern.interactiveHero.hero.entryCards.create.title",
    subtitleKey: "modern.interactiveHero.hero.entryCards.create.subtitle",
    icon: "ion:videocam-outline",
    action: "post_recording",
  },
  {
    id: "learning",
    titleKey: "modern.interactiveHero.hero.entryCards.learn.title",
    subtitleKey: "modern.interactiveHero.hero.entryCards.learn.subtitle",
    icon: "ion:book-outline",
    action: "generate_quiz",
  },
];

const modelFamilies: ModelFamily[] = [
  {
    id: "standard",
    label: "Standard",
    detail: "Selected",
    icon: "ion:sparkles-outline",
    color: "#4ea4f5",
  },
  {
    id: "claude-4-8-opus",
    label: "Claude 4.8 Opus",
    detail: "Anthropic models",
    icon: "simple-icons:claude",
    color: "#0d0d0d",
  },
  {
    id: "gemini-3-1-pro",
    label: "Gemini 3.1 Pro",
    detail: "Google models",
    icon: "simple-icons:googlegemini",
    color: "#4285f4",
  },
];

const moreModelFamilies: ModelFamily[] = [
  {
    id: "gpt-5-4",
    label: "GPT-5.4",
    detail: "OpenAI models",
    icon: "simple-icons:openai",
    color: "#0d0d0d",
  },
  {
    id: "claude-4-5-haiku",
    label: "Claude 4.5 Haiku",
    detail: "Anthropic models",
    icon: "simple-icons:claude",
    color: "#0d0d0d",
  },
  {
    id: "claude-4-6-sonnet",
    label: "Claude 4.6 Sonnet",
    detail: "Anthropic models",
    icon: "simple-icons:claude",
    color: "#0d0d0d",
  },
  {
    id: "gemini-3-1-flash",
    label: "Gemini 3.1 Flash",
    detail: "Google models",
    icon: "simple-icons:googlegemini",
    color: "#4285f4",
  },
  {
    id: "gemini-3-5-flash",
    label: "Gemini 3.5 Flash",
    detail: "Google models",
    icon: "simple-icons:googlegemini",
    color: "#4285f4",
  },
  {
    id: "grok-4-3",
    label: "Grok 4.3",
    detail: "xAI models",
    icon: "simple-icons:x",
    color: "#0d0d0d",
  },
];

const platformCards: PlatformCard[] = [
  {
    id: "ios",
    titleKey: "modern.interactiveHero.hero.platforms.ios",
    icon: "ion:logo-apple",
    feature: "ios_app",
  },
  {
    id: "web",
    titleKey: "modern.interactiveHero.hero.platforms.web",
    icon: "ion:arrow-forward",
    feature: "web_app",
    cta: true,
  },
  {
    id: "desktop",
    titleKey: "modern.interactiveHero.hero.platforms.mac",
    icon: "ion:desktop-outline",
    feature: "desktop_app",
  },
];

const renderHomeActionIcon = (action: HomeActionCard) => {
  if (action.imageSrc) {
    return <img alt="" aria-hidden="true" className="h-[34px] w-[34px] object-contain" src={action.imageSrc} />;
  }

  return <Icon className="h-[34px] w-[34px] text-[#5a5a5d]" icon={action.icon ?? "ion:sparkles-outline"} />;
};

const InteractiveHero: React.FC = () => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState("");
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isModelMenuOpen, setIsModelMenuOpen] = useState(false);
  const [isMoreModelMenuOpen, setIsMoreModelMenuOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const canSend = prompt.trim().length > 0;

  const goToSlide = useCallback(
    (index: number) => {
      setActiveSlide(index);
    },
    []
  );

  const goNext = useCallback(() => {
    setActiveSlide((prev) => (prev + 1) % SLIDE_COUNT);
  }, []);

  const goPrev = useCallback(() => {
    setActiveSlide((prev) => (prev - 1 + SLIDE_COUNT) % SLIDE_COUNT);
  }, []);

  useEffect(() => {
    if (isPaused || prefersReducedMotion) return;
    const timer = setInterval(goNext, AUTO_ROTATE_MS);
    return () => clearInterval(timer);
  }, [goNext, isPaused, prefersReducedMotion]);

  useEffect(() => {
    if (!isModelMenuOpen) return;

    const closeModelMenu = () => {
      setIsModelMenuOpen(false);
      setIsMoreModelMenuOpen(false);
    };

    window.addEventListener("click", closeModelMenu);
    return () => window.removeEventListener("click", closeModelMenu);
  }, [isModelMenuOpen]);

  const slides = [
    <FeatureSectionAIDemo key="ai-demo" autoStart />,
    <FeatureSectionRecording key="recording" autoStart />,
    <FeatureSectionPDF key="pdf" autoStart />,
    <FeatureSectionAgentEnterprise key="agent-enterprise" autoStart />,
  ];

  const handlePromptSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    redirectForChat(
      prompt.trim() ||
        String(t("modern.interactiveHero.hero.promptFallback", "Help me turn my context into useful work"))
    );
  };

  const handleHomeActionClick = (action: FeatureAction) => {
    const message = prompt.trim();
    redirectToApp({
      action,
      redirect_reason: "try_feature",
      feature: action,
      message: message || undefined,
    });
  };

  const handleModelSelect = (model: ModelFamily) => {
    const message = prompt.trim();
    redirectToApp({
      action: "select_model",
      redirect_reason: "select_model",
      feature: model.id,
      message: message || undefined,
    });
  };

  const handlePlatformClick = (platform: PlatformCard) => {
    const message = prompt.trim();
    redirectToApp({
      action: "chat",
      redirect_reason: "try_feature",
      feature: platform.feature,
      message: message || undefined,
    });
  };

  return (
    <div className="relative w-full overflow-hidden bg-white">
      <section className="relative z-10 pb-8 pt-20 md:pb-10 md:pt-24">
        <div className="mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
          <div className="mx-auto flex w-full max-w-6xl flex-col items-center text-center">
            <motion.h1
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 w-full max-w-[1120px] text-[27px] font-bold leading-[1.08] sm:text-[35px] md:text-[38px] lg:text-[52px]"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
              transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.1 }}
            >
              <span className="text-gray-900">{t("modern.interactiveHero.hero.title")}</span>{" "}
              <span className="gradient-text whitespace-nowrap" style={{ letterSpacing: 0 }}>
                {t("modern.interactiveHero.hero.titleHighlight")}
              </span>
            </motion.h1>

            <motion.p
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto mb-6 max-w-3xl"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
              style={{ color: "#726F6F", fontSize: "20px" }}
              transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.2 }}
            >
              {t("modern.interactiveHero.hero.subtitle")}
            </motion.p>

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto mb-4 flex w-full max-w-[860px] flex-nowrap items-center justify-start gap-2 overflow-x-auto px-1 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] sm:grid sm:grid-cols-5 sm:overflow-visible sm:px-0 sm:pb-0 lg:gap-4 [&::-webkit-scrollbar]:hidden"
              id="note-action-card-panel"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.28 }}
            >
              {homeActionCards.map((action) => (
                <button
                  className="h-[112px] w-[110px] shrink-0 cursor-pointer appearance-none rounded-[16px] border border-transparent bg-[#f8f8f8] px-2 py-1 shadow-[0_1px_3px_rgba(15,15,15,0.06)] transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-[#f3f3f5] hover:shadow-[0_3px_8px_rgba(15,15,15,0.08)] active:scale-[0.97] active:bg-[#e5e5ea] motion-reduce:transform-none motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4ea4f5] sm:w-full"
                  data-testid={`button_home_feature_${action.id}`}
                  id={`button_home_feature_${action.id}`}
                  key={action.id}
                  onClick={() => handleHomeActionClick(action.action)}
                  type="button"
                >
                  <span className="flex h-full flex-col items-center justify-center">
                    <span className="flex h-[46px] w-12 items-center justify-center">
                      {renderHomeActionIcon(action)}
                    </span>
                    <span className="mt-1.5 text-center text-[16px] font-semibold leading-[18px] text-[#0d0d0d]">
                      {t(action.titleKey)}
                    </span>
                    <span className="mt-0.5 max-w-none whitespace-nowrap text-center text-[12px] font-normal leading-[14px] text-[#979797]">
                      {t(action.subtitleKey)}
                    </span>
                  </span>
                </button>
              ))}
            </motion.div>

            <motion.form
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto w-full max-w-[860px] overflow-visible bg-transparent text-left"
              data-testid="chat-message-bar"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
              onSubmit={handlePromptSubmit}
              transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.3 }}
            >
              <div className="relative flex w-full flex-col gap-0 outline-none" tabIndex={-1}>
                <div className="relative w-full">
                  <div className="relative flex flex-col overflow-visible rounded-[24px] border border-solid border-[#c4c4c7] bg-white pb-0.5 pl-[14px] pr-2 pt-[3px] shadow-[0px_4px_10px_rgba(0,0,0,0.04)]">
                    <div className="flex min-h-[48px] items-start gap-2">
                      <div className="relative max-h-[320px] min-h-[48px] w-full flex-1 overflow-hidden">
                        {!prompt ? (
                          <div className="pointer-events-none absolute left-0 right-0 top-3.5 text-[16px] font-normal leading-6 text-[#979797]">
                            {t("modern.interactiveHero.hero.promptPlaceholder", "Ask anything")}
                          </div>
                        ) : null}
                        <textarea
                          aria-label={String(t("modern.interactiveHero.hero.promptLabel", "Ask anything"))}
                          className="block h-[48px] max-h-[320px] w-full resize-none appearance-none overflow-auto border-0 bg-transparent px-0 pb-2.5 pt-3.5 text-[16px] font-normal leading-6 text-[#0d0d0d] outline-none"
                          onChange={(event) => setPrompt(event.target.value)}
                          placeholder=""
                          rows={1}
                          value={prompt}
                        />
                      </div>
                    </div>

                    <div className="relative z-40 overflow-visible opacity-100">
                      <div className="flex h-11 items-center justify-between gap-2">
                        <div className="flex min-w-0 flex-1 shrink items-center gap-0">
                          <button
                            aria-label={String(t("modern.interactiveHero.hero.addContextLabel", "Add context"))}
                            className="flex h-11 min-h-11 w-11 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-[12px] border-0 bg-transparent text-[#0d0d0d] transition-colors hover:bg-[#f4f5f7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4ea4f5]"
                            data-testid="button_chat_add_context"
                            onClick={() => handleHomeActionClick("attach_file")}
                            type="button"
                          >
                            <Icon className="h-5 w-5" icon="ion:add" />
                          </button>

                          <div className="relative min-w-0 overflow-visible">
                            <button
                              aria-expanded={isModelMenuOpen}
                              aria-label={String(t("modern.interactiveHero.hero.aiMode", "Standard"))}
                              className="flex h-11 min-h-11 min-w-0 appearance-none items-center gap-1.5 rounded-[12px] border-0 border-transparent bg-transparent px-[14px] py-2 text-[#0d0d0d] transition-colors hover:bg-[#f4f5f7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4ea4f5]"
                              data-component="model-selector"
                              onClick={(event) => {
                                event.stopPropagation();
                                setIsModelMenuOpen((isOpen) => {
                                  const nextOpen = !isOpen;
                                  setIsMoreModelMenuOpen(false);
                                  return nextOpen;
                                });
                              }}
                              type="button"
                            >
                              <img alt="" aria-hidden="true" className="mr-1 h-4 w-4 object-contain" src="/logo/logo.png" />
                              <span className="truncate text-[14px] font-normal leading-5">
                                {t("modern.interactiveHero.hero.aiMode", "Standard")}
                              </span>
                              <Icon
                                className="h-4 w-4 shrink-0 text-[#979797]"
                                icon={isModelMenuOpen ? "ion:chevron-up" : "ion:chevron-down"}
                              />
                            </button>

                            <AnimatePresence>
                              {isModelMenuOpen ? (
                                <motion.div
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  className="absolute bottom-11 left-0 z-50 w-[min(196px,calc(100vw-64px))] min-w-[196px] overflow-visible rounded-[12px] border border-[#eee] bg-white py-1 shadow-[0_8px_18px_rgba(15,15,15,0.12)]"
                                  exit={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 6, scale: 0.98 }}
                                  initial={prefersReducedMotion ? false : { opacity: 0, y: 6, scale: 0.98 }}
                                  transition={{ duration: prefersReducedMotion ? 0 : 0.16 }}
                                  data-testid="landing_model_selector_menu"
                                >
                                  <button
                                    className="flex min-h-[34px] w-full cursor-pointer appearance-none items-center justify-between gap-2 rounded-[8px] border-0 bg-[#f1eff4] px-3 py-1.5 text-left transition-colors hover:bg-[#ebe9ef] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4ea4f5]"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleModelSelect(modelFamilies[0]);
                                    }}
                                    type="button"
                                  >
                                    <span className="flex min-w-0 items-center gap-2">
                                      <img alt="" aria-hidden="true" className="h-[18px] w-[18px] object-contain" src="/logo/logo.png" />
                                      <span className="truncate text-[14px] font-medium leading-5 text-[#0d0d0d]">
                                        {t("modern.interactiveHero.hero.autoModel")}
                                      </span>
                                    </span>
                                    <Icon className="h-4 w-4 shrink-0 text-[#0d0d0d]" icon="ion:checkmark" />
                                  </button>

                                  <div className="mt-0.5 flex w-full flex-col gap-0.5">
                                    {modelFamilies.slice(1).map((model) => (
                                      <button
                                        className="flex min-h-[34px] w-full cursor-pointer appearance-none items-center justify-between gap-2 rounded-[8px] border-0 bg-transparent px-3 py-1.5 text-left transition-colors hover:bg-[#f4f5f7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4ea4f5]"
                                        key={model.id}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleModelSelect(model);
                                        }}
                                        type="button"
                                      >
                                        <span className="flex min-w-0 items-center gap-2">
                                          <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center">
                                            <Icon className="h-[18px] w-[18px]" icon={model.icon} style={{ color: model.color }} />
                                          </span>
                                          <span className="truncate text-[14px] font-normal leading-5 text-[#0d0d0d]">
                                            {model.label}
                                          </span>
                                        </span>
                                      </button>
                                    ))}
                                  </div>

                                  <div
                                    className="relative mt-1 w-full overflow-visible"
                                    onPointerEnter={() => setIsMoreModelMenuOpen(true)}
                                  >
                                    <button
                                      className={`flex min-h-[34px] w-full cursor-pointer appearance-none items-center justify-between rounded-[8px] border-0 px-3 py-1.5 text-left transition-colors hover:bg-[#f4f5f7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4ea4f5] ${
                                        isMoreModelMenuOpen ? "bg-[#f4f5f7]" : "bg-transparent"
                                      }`}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setIsMoreModelMenuOpen((isOpen) => !isOpen);
                                      }}
                                      type="button"
                                    >
                                      <span className="text-[14px] font-normal leading-5 text-[#0d0d0d]">
                                        {t("modern.interactiveHero.hero.moreModels")}
                                      </span>
                                      <Icon className="h-4 w-4 text-[#0d0d0d]" icon={isMoreModelMenuOpen ? "ion:chevron-back" : "ion:chevron-forward"} />
                                    </button>

                                    {isMoreModelMenuOpen ? (
                                      <div
                                        className="mt-1 flex w-full flex-col gap-0.5 rounded-[12px] border border-[#eee] bg-white py-1 shadow-[0_8px_18px_rgba(15,15,15,0.12)] sm:absolute sm:bottom-0 sm:left-[calc(100%+4px)] sm:mt-0 sm:w-[220px]"
                                        data-testid="landing_model_selector_more_menu"
                                      >
                                        {moreModelFamilies.map((model) => (
                                          <button
                                            className="flex min-h-[34px] w-full cursor-pointer appearance-none items-center gap-2 rounded-[8px] border-0 bg-transparent px-3 py-1.5 text-left transition-colors hover:bg-[#f4f5f7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4ea4f5]"
                                            key={model.id}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              handleModelSelect(model);
                                            }}
                                            type="button"
                                          >
                                            <Icon className="h-[18px] w-[18px] shrink-0" icon={model.icon} style={{ color: model.color }} />
                                            <span className="truncate text-[14px] font-normal leading-5 text-[#0d0d0d]">
                                              {model.label}
                                            </span>
                                          </button>
                                        ))}
                                      </div>
                                    ) : null}
                                  </div>

                                  <div className="my-1 h-px w-full bg-[#eee]" />

                                  <button
                                    className="flex min-h-[34px] w-full cursor-pointer appearance-none items-center justify-between gap-2 rounded-[8px] border-0 bg-transparent px-3 py-1.5 text-left transition-colors hover:bg-[#f4f5f7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4ea4f5]"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleModelSelect({
                                        id: "thinking-max",
                                        label: String(t("modern.interactiveHero.hero.thinkingMode")),
                                        detail: String(t("modern.interactiveHero.hero.maxMode")),
                                        icon: "ion:extension-puzzle-outline",
                                        color: "#0d0d0d",
                                      });
                                    }}
                                    type="button"
                                  >
                                    <span className="flex min-w-0 items-center gap-2">
                                      <Icon className="h-[18px] w-[18px] shrink-0 text-[#0d0d0d]" icon="ion:extension-puzzle-outline" />
                                      <span className="truncate text-[14px] font-normal leading-5 text-[#0d0d0d]">
                                        {t("modern.interactiveHero.hero.thinkingMode")}
                                      </span>
                                    </span>
                                    <span className="flex items-center gap-2 text-[12px] leading-4 text-[#666]">
                                      {t("modern.interactiveHero.hero.maxMode")}
                                      <Icon className="h-4 w-4 text-[#0d0d0d]" icon="ion:chevron-forward" />
                                    </span>
                                  </button>
                                </motion.div>
                              ) : null}
                            </AnimatePresence>
                          </div>
                        </div>

                        <div className="flex h-11 shrink-0 items-center gap-0">
                          <button
                            aria-disabled={!canSend}
                            aria-label={String(t("modern.interactiveHero.hero.sendLabel", "Send chat message"))}
                            className={`flex h-9 w-9 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-[20px] border border-solid bg-white p-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4ea4f5] ${
                              canSend
                                ? "border-[#4ea4f5] opacity-100"
                                : "border-[#c4c4c7] opacity-[0.35] hover:opacity-60"
                            }`}
                            data-testid="button_chat_send_message"
                            type="submit"
                          >
                            <span className="flex h-5 w-5 items-center justify-center">
                              <Icon className="h-[18px] w-[18px] text-[#4ea4f5]" icon="ion:arrow-up" />
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.form>

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 flex w-full max-w-[520px] flex-nowrap items-center justify-center gap-2.5 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:overflow-visible sm:px-0 sm:pb-0 [&::-webkit-scrollbar]:hidden"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
              transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.4 }}
            >
              {platformCards.map((platform) => (
                <button
                  className={`flex h-10 shrink-0 cursor-pointer appearance-none items-center justify-center gap-2 rounded-full border px-4 text-center transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4ea4f5] ${
                    platform.cta
                      ? "min-w-[142px] border-[#4ea4f5] bg-white text-[#226fb7] hover:border-[#226fb7] hover:bg-[#f4f5f7]"
                      : "min-w-[92px] border-[#c4c4c7] bg-white text-[#0d0d0d] hover:border-[#a3acb9] hover:bg-[#f4f5f7]"
                  }`}
                  key={platform.id}
                  onClick={() => handlePlatformClick(platform)}
                  type="button"
                >
                  <Icon
                    className="h-4 w-4 shrink-0"
                    icon={platform.icon}
                  />
                  <span className="whitespace-nowrap text-[13px] font-semibold leading-4">
                    {t(platform.titleKey)}
                  </span>
                </button>
              ))}
            </motion.div>
            <motion.p
              animate={{ opacity: 1, y: 0 }}
              className="m-0 mt-1.5 text-[12px] leading-4 text-[#7d7d80]"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
              transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.46 }}
            >
              {t("modern.interactiveHero.hero.platformAvailability")}
            </motion.p>
          </div>
        </div>
      </section>

      <section
        className="relative mt-2 md:mt-4"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="relative overflow-hidden" style={{ minHeight: "auto" }}>
          <div className="w-full" key={activeSlide}>
            {slides[activeSlide]}
          </div>
        </div>

        <button
          aria-label={String(t("modern.interactiveHero.carousel.previous", "Previous feature demo"))}
          className="absolute left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-none bg-white/80 shadow-md transition-colors hover:bg-white md:left-6"
          onClick={goPrev}
          type="button"
        >
          <Icon className="text-xl text-gray-700" icon="lucide:chevron-left" />
        </button>
        <button
          aria-label={String(t("modern.interactiveHero.carousel.next", "Next feature demo"))}
          className="absolute right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-none bg-white/80 shadow-md transition-colors hover:bg-white md:right-6"
          onClick={goNext}
          type="button"
        >
          <Icon className="text-xl text-gray-700" icon="lucide:chevron-right" />
        </button>

        <div className="flex justify-center gap-2 py-3">
          {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
            <button
              aria-label={String(t("modern.interactiveHero.carousel.showSlide", "Show feature demo {{index}}", { index: i + 1 }))}
              className={`cursor-pointer rounded-full border-none transition-all duration-300 ${
                i === activeSlide
                  ? "h-2.5 w-8 bg-cornflowerblue"
                  : "h-2.5 w-2.5 bg-gray-300 hover:bg-gray-400"
              }`}
              data-testid={`button_feature_demo_${i + 1}`}
              key={i}
              onClick={() => goToSlide(i)}
              type="button"
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default InteractiveHero;
